// Módulo Eventos: cadastro de eventos + Pedido de Entrega (convites).
// O Pedido de Entrega NÃO é pedido de venda — estrutura própria, fora de relatórios
// de venda/notificações. Espelha os DTOs de ERPApi/internal/core/domain/delivery_order.go.

export interface Event {
  event_id: number;
  name: string;
  description?: string;
  start_date?: string; // "YYYY-MM-DD"
  end_date?: string;
  addr_street?: string;
  addr_num?: string;
  addr_compl?: string;
  addr_district?: string;
  addr_city?: string;
  addr_state?: string;
  addr_zip?: string;
  latitude?: number;
  longitude?: number;
  geocoding_status?: number; // 0 = sucesso | 1 = falha
  invitation_product_sku?: string;
  default_volume_height?: number;
  default_volume_width?: number;
  default_volume_length?: number;
  default_volume_weight?: number;
  company_id?: number;
  is_active: boolean;
}

export type EventFormData = Omit<Event, 'event_id' | 'latitude' | 'longitude' | 'geocoding_status' | 'is_active'> & {
  is_active?: boolean;
};

// Convidado devolvido pela busca no CRM (clientes + personal organizers) via gRPC.
export interface CRMPerson {
  kind: 'customer' | 'commercial_partner';
  id: number;
  name: string;
  document?: string;
  person_type?: number;
  cell_phone?: string;
  phone?: string;
  email?: string;
  addr_street?: string;
  addr_num?: string;
  addr_compl?: string;
  addr_district?: string;
  addr_city?: string;
  addr_state?: string;
  addr_zip?: string;
}

// Status do Pedido de Entrega ([Financial].[DeliveryOrder].StatusId)
export const DELIVERY_ORDER_STATUS = {
  PENDING: 1,
  SHIPPED: 2,
  DELIVERED: 3,
  CANCELED: 9,
} as const;

export interface DeliveryOrder {
  delivery_order_id: number;
  delivery_order_type: number; // 1 = Convite de evento
  event_id?: number;
  recipient_name: string;
  recipient_document?: string;
  recipient_phone?: string;
  recipient_email?: string;
  crm_customer_id?: number;
  crm_comercial_partner_id?: number;
  ship_addr_street?: string;
  ship_addr_num?: string;
  ship_addr_compl?: string;
  ship_addr_district?: string;
  ship_addr_city?: string;
  ship_addr_state?: string;
  ship_addr_zip?: string;
  status_id: number;
  status_label?: string;
  provider_id?: number;
  provider_service_id?: number;
  invoice_id?: number;
  // Situação da NF vinculada (2 = Cancelada) — é o que libera o desvínculo na tela.
  invoice_status_id?: number;
  invoice_status_label?: string;
  delivered_at?: string;
  // Comparecimento ao EVENTO — não confundir com status_id, que é a etapa logística do
  // convite (o convite chega na casa da convidada dias antes; ter sido entregue não diz se
  // ela apareceu). null/undefined = não informado, true = compareceu, false = não compareceu.
  attended?: boolean | null;
  attended_at?: string;
  attended_by?: string;
  observation?: string;
  event_name?: string;
  provider_name?: string;
}

/** Filtro de comparecimento da listagem de convites. */
export type AttendedFilter = 'true' | 'false' | 'unknown';

/** Contadores de comparecimento da listagem inteira (não só da página). */
export interface DeliveryOrderAttendanceSummary {
  attended: number;
  not_attended: number;
  unknown: number;
}

export interface DeliveryOrderVolume {
  delivery_order_volume_id: number;
  delivery_order_id: number;
  height: number;
  width: number;
  length: number;
  diameter: number;
  weight: number;
  volume_number?: number;
}

export interface DeliveryOrderFreightQuote {
  delivery_order_freight_quote_id: number;
  delivery_order_id: number;
  provider_id?: number;
  provider_service_id?: number;
  cost?: number;
  delivery_days?: number;
  is_selected: boolean;
  provider_name?: string;
  provider_service_name?: string;
  /** Opção "Entrega própria (sem cotação)" da frota própria (derivado no ERPApi). */
  is_manual?: boolean;
  /** Transportadora/serviço bloqueado para a empresa (roteamento de pagamentos); não pode ser selecionada. */
  blocked?: boolean;
  blocked_reason?: string | null;
}

export interface DeliveryOrderCorreiosObject {
  codigo_objeto: string;
  volume_number: number;
  total_volumes: number;
  tracking_status?: string;
  tracking_url: string;
}

// Resposta do despacho Correios em 1 clique (POST /delivery-orders/:id/dispatch-correios).
// Os flags dizem o que ESTA chamada fez (false = passo já estava feito — idempotente).
export interface DeliveryOrderDispatchResponse {
  delivery_order_id: number;
  invoice_id: number;
  invoice_emitted: boolean;
  shipping_integrated: boolean;
  codigo_objetos?: string[];
}

export interface DeliveryOrderTracking {
  delivery_order_id: number;
  status_id: number;
  status_label: string;
  invoice_id?: number;
  correios_objects?: DeliveryOrderCorreiosObject[];
  correios_tracking_url?: string;
  delivered_at?: string;
  delivery_contact?: string;
  delivery_contact_document?: string;
  delivery_photo_urls?: string[];
}

export interface RoutePlanningInvitation {
  delivery_order_id: number;
  recipient_name: string;
  address_text: string;
  city: string;
  latitude?: number;
  longitude?: number;
  geocoding_status: number;
  geocoding_message?: string;
}

export interface RoutePlanningResponse {
  cities: string[];
  city?: string;
  invitations?: RoutePlanningInvitation[];
}
